import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Badge } from '../ui/Badge';

interface UseCase {
  id: string;
  title: string;
  description: string;
  businessScenario: string;
  recommendedFor: string[];
  icon?: string;
}

interface UseCaseCardProps {
  useCase: UseCase;
  onEdit?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}

export function UseCaseCard({ useCase, onEdit, onDelete, readOnly = false }: UseCaseCardProps) {
  // Dynamically get icon from Lucide
  const IconComponent = useCase.icon 
    ? (LucideIcons as any)[useCase.icon] || LucideIcons.Lightbulb
    : LucideIcons.Lightbulb;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <IconComponent className="w-5 h-5 text-blue-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-gray-900 truncate">{useCase.title}</h4>
            
            {!readOnly && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {onEdit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Editar"
                  >
                    <LucideIcons.Pencil className="w-4 h-4" />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={onDelete}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remover"
                  >
                    <LucideIcons.Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
            {useCase.description}
          </p>

          {/* Business Scenario */}
          <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
            <span className="text-gray-500 font-medium">Cenário: </span>
            <span className="text-gray-700">{useCase.businessScenario}</span>
          </div>

          {/* Recommended For */}
          {useCase.recommendedFor.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {useCase.recommendedFor.map((item, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {item}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UseCaseCard;
